import { ProLightClock } from '@nexelem/icons/ProLightClock';
import { type Control, type FieldValues, FormProvider, type Path, useController, useForm } from 'react-hook-form';
import { FormCalendarInput } from '../FormCalendarInput';
import { FormTextInput } from '../FormTextInput';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { set } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import type { TextInputSize } from '@nexelem/components';

type FormDateTimeInputProps<T extends FieldValues> = {
  name: Path<T>;
  control?: Control<T>;
  className?: string;
  required?: boolean;
  dateLabel?: string;
  timeLabel?: string;
  size?: TextInputSize;
};

export const FormDateTimeInput = <T extends FieldValues>({
  control,
  name,
  className,
  required,
  dateLabel,
  timeLabel,
  size,
}: FormDateTimeInputProps<T>) => {
  const { t } = useTranslation();

  const {
    field: { value, onChange: setFieldValue },
    fieldState,
  } = useController({ name, control });

  const form = useForm({
    resolver: zodResolver(dateTimeSchema),
    defaultValues: parseDefaultValues(value),
    mode: 'onChange',
  });

  useEffect(() => {
    const unsub = form.subscribe({
      formState: {
        values: true,
        isValid: true,
      },
      callback: ({ values, isValid }) => {
        const { date, time } = values;

        if (isValid && date && time) {
          const [hours, minutes] = time.split(':');
          const output = set(date, { hours: Number(hours), minutes: Number(minutes), seconds: 0 });
          setFieldValue(output);
        } else {
          setFieldValue(null);
        }
      },
    });

    return () => unsub();
  }, [form, setFieldValue]);

  const fieldErrorMessage = fieldState.error?.message;
  const hasOuterError = !!fieldState.error;

  useEffect(() => {
    if (hasOuterError) {
      form.trigger();
    }
  }, [hasOuterError, form]);

  return (
    <FormProvider {...form}>
      <div className={className}>
        <div className="flex items-end gap-5">
          <FormCalendarInput
            className="max-w-56"
            label={dateLabel ?? `${t('common.date')}`}
            name="date"
            required={required}
            showErrorMessage={false}
            size={size}
          />
          <FormTextInput
            label={timeLabel ?? `${t('common.hour')}`}
            maxWidth={156}
            name="time"
            placeholder="00:00"
            showErrorMessage={false}
            size={size}
            StartIcon={ProLightClock}
            type="time"
          />
        </div>
        {fieldErrorMessage && (
          <div className="mt-2 ml-2 flex items-center gap-2 text-t14-sb">
            <FontAwesomeIcon className="h-5 w-5 text-icon-error" icon={faCircleExclamation} />
            <span className="text-text-state-error-light">{fieldErrorMessage}</span>
          </div>
        )}
      </div>
    </FormProvider>
  );
};

const dateTimeSchema = z.object({
  date: z.date(),
  time: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/),
});

const parseDefaultValues = (value: Date | undefined) => {
  return {
    date: value ?? null,
    time: value?.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }) || '',
  };
};
